import React, { useState } from 'react';
import { ExternalLink, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nivadigital.io';

// Blog post data
export const blogPosts = [
  {
    id: 7,
    title: "Beyond the Cloud: How On-Prem Enterprises Are Using AI to Grow Revenue, Not Cut Headcount",
    excerpt: "There's a tired narrative that AI adoption means fewer jobs. But for enterprises with significant on-premises footprints, banks, insurers, hospitals, manufacturers, AI isn't arriving as a replacement for staff. It's arriving as a force multiplier for process, speed, and revenue.",
    category: "AI",
    author: "Chengappa MG",
    date: "Jul 2026",
    readTime: "4 min",
    featured: true,
    url: "https://medium.com/@chengappa_58948/beyond-the-cloud-how-on-prem-enterprises-are-using-ai-to-grow-revenue-not-cut-headcount-89efd70127bf",
    image: "/blog-onprem.png"
  },
  {
    id: 5,
    title: "The Rise of World-Class AI-Enabled Teams: Why Companies Are Racing to Transform How They Deliver",
    excerpt: "Not long ago, the measure of a great engineering team was headcount. The more senior engineers you could hire, the faster you could ship. Today, that equation has fundamentally changed. The best-performing technology teams in 2026 aren't necessarily the largest—they're the most intelligently augmented.",
    category: "AI",
    author: "Chengappa MG",
    date: "Apr 2026",
    readTime: "7 min",
    featured: false,
    url: "https://medium.com/@chengappa_58948/the-rise-of-world-class-ai-enabled-teams-why-companies-are-racing-to-transform-how-they-deliver-9b1e65068b56",
    image: "/blog-featured.jpg"
  },
  {
    id: 6,
    title: "From Claims to Compliance: How AI is Quietly Transforming the Insurance Value Chain",
    excerpt: "There is a quiet reckoning happening inside the insurance industry. AI is reshaping every link in the value chain, from intelligent claims processing and fraud detection to KYC automation and churn prevention.",
    category: "AI",
    author: "Chengappa MG",
    date: "Mar 2026",
    readTime: "4 min",
    featured: false,
    url: "https://medium.com/@chengappa_58948/from-claims-to-compliance-how-ai-is-quietly-transforming-the-insurance-value-chain-841d76fac60e",
    image: "/blog-3.jpg"
  },
  {
    id: 1,
    title: "How We Help Clients Save 40–60% Without Cutting Corners",
    excerpt: "The math behind savings is well-known. The methodology behind achieving it sustainably, with quality intact, is not. We break it down in detail.",
    category: "Cost Optimisation",
    author: "Chengappa MG",
    date: "Dec 2024",
    readTime: "6 min",
    featured: false,
    url: "https://medium.com/@chengappa_58948/we-almost-lost-a-client-before-we-even-started-c20e4461bc61",
    image: "/blog-5.jpg"
  },
  {
    id: 2,
    title: "Your GCC is Live. Now, How Do You Make It Lean?",
    excerpt: "Setting up a Global Capability Centre is a milestone. But most GCCs spend their first three to five years operating at 60 to 70 percent of their potential efficiency. Here's where to look for hidden cost drivers.",
    category: "Cost Optimisation",
    author: "Chengappa MG",
    date: "Mar 2026",
    readTime: "4 min",
    featured: false,
    url: "https://medium.com/@chengappa_58948/your-gcc-is-live-now-how-do-you-make-it-lean-97ea20c8e91f",
    image: "/blog-2.jpg"
  },
  {
    id: 3,
    title: "The New Org Chart: What It Looks Like When AI Joins Your Team",
    excerpt: "Everyone is talking about AI. Very few companies are working with it. The gap between organisations that talk about AI and those genuinely working with it is not a gap in technology—it's a gap in operating model design.",
    category: "Leadership",
    author: "Chengappa MG",
    date: "Mar 2026",
    readTime: "4 min",
    featured: false,
    url: "https://medium.com/@chengappa_58948/the-new-org-chart-what-it-looks-like-when-ai-joins-your-team-8cd3bedd310e",
    image: "/blog-3.jpg"
  },
  {
    id: 4,
    title: "Why Smart Companies Are Ditching Traditional Outsourcing for Pods",
    excerpt: "Traditional outsourcing was built for a different era. The problem was never offshoring. The problem was the vendor model that wrapped around it. Here's why forward-thinking companies are making the switch.",
    category: "Teams",
    author: "Chengappa MG",
    date: "Mar 2026",
    readTime: "4 min",
    featured: false,
    url: "https://medium.com/@chengappa_58948/why-smart-companies-are-ditching-traditional-outsourcing-for-offshore-pods-34d6a3ace23c",
    image: "/blog-4.jpg"
  }
];

const categories = [
  "All Posts",
  "Client Management", 
  "Cost Optimisation",
  "AI",
  "Teams",
  "Leadership"
];

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState("All Posts");
  const [email, setEmail] = useState("");

  const filteredPosts = activeCategory === "All Posts" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Blog — Insights & Delivery Stories | NIVA Digital AI"
        description="Real lessons from building, operating, and transferring world-class AI-enabled teams. No fluff, no jargon - just practical insights from the trenches of delivery."
        keywords="teams blog, GCC insights, DevOps blog, CloudOps articles, infrastructure teams, delivery"
        canonical={`${BASE_URL}/blog`}
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            From the <em className="not-italic text-red-500">Trenches</em> of<br />Delivery
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
            Real lessons from building, operating, and transferring world-class AI-enabled teams.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-6 px-4 border-b border-gray-200 bg-white sticky top-[72px] z-20">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === category
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Featured Section */}
          {filteredPosts.filter(post => post.featured).length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-8">
              <span className="text-xs font-bold tracking-wider text-red-600 uppercase">Featured</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            {/* Featured Card */}
            {filteredPosts.filter(post => post.featured).map((post) => (
              <a
                key={post.id}
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white rounded-2xl border-2 border-gray-100 hover:border-red-200 overflow-hidden transition-all duration-300 hover:shadow-xl"
              >
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Image */}
                  <div className="relative h-64 md:h-80 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                  </div>

                  {/* Content */}
                  <div className="p-8 flex flex-col justify-center">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-red-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden">
                          <img 
                            src="/chengappa-mg.png" 
                            alt={post.author}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{post.author}</div>
                          <div className="text-xs text-gray-500">{post.date}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Clock className="w-4 h-4" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
          )}

          {/* Other Posts Grid */}
          {filteredPosts.filter(post => !post.featured).length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-4 mb-8">
                <span className="text-xs font-bold tracking-wider text-gray-600 uppercase">Latest Articles</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.filter(post => !post.featured).map((post) => (
                  <a
                    key={post.id}
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-white rounded-xl border border-gray-200 hover:border-red-200 overflow-hidden transition-all duration-300 hover:shadow-lg"
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-block px-2 py-1 bg-white/90 backdrop-blur-sm text-red-600 text-xs font-semibold rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full overflow-hidden">
                            <img 
                              src="/chengappa-mg.png" 
                              alt={post.author}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-900">{post.author}</div>
                            <div className="text-xs text-gray-500">{post.date}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-gray-400 text-xs">
                          <Clock className="w-3 h-3" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Strip - Newsletter */}
      <section className="py-16 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Get NIVA Insights in Your Inbox
              </h2>
              <p className="text-white/80">
                One issue per month. Practical lessons from running AI-enabled teams at scale.
              </p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 flex-1 md:w-64"
              />
              <Link to="/contact">
                <Button className="bg-white text-red-600 hover:bg-gray-100 whitespace-nowrap">
                  Subscribe
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Build Your Team?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Let&apos;s discuss how NIVA Digital can help you build world-class AI-enabled teams at scale.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
              Schedule Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
